import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText, Shield, BookOpen, Sparkles, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


import { useTranslation } from 'react-i18next';


export default function TermsConsentModal({ onAccepted }) {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/accept-terms`, { version: '2.0' }, { withCredentials: true });
      onAccepted?.();
    } catch {
      // If not authenticated (guest), still mark locally
      onAccepted?.();
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4" data-testid="terms-consent-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md md:max-w-xl lg:max-w-2xl rounded-3xl p-6 md:p-8 shadow-2xl animate-in zoom-in-95 fade-in duration-300 overflow-y-auto"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, rgba(51,65,85,0.4))',
          scrollbarGutter: 'stable',
          maxHeight: 'min(90vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        <div className="text-center mb-5">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(99,102,241,0.12)' }}
          >
            <ScrollText className="h-7 w-7" style={{ color: 'var(--theme-accent, #6366f1)' }} />
          </div>
          <h2
            className="text-lg font-bold"
            style={{ color: 'var(--theme-text, #fff)' }}
            data-testid="terms-consent-title"
          >
            {t('generated.termsconsentmodal_before_you_continue_093e')}
          </h2>
          <p
            className="text-sm mt-2 leading-relaxed"
            style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}
          >
            {t('generated.termsconsentmodal_please_review_how_43d6')}
          </p>
        </div>

        {/* How Eili uses AI — grounded sources first, then data sharing, then disclaimer */}
        <div
          className="rounded-xl p-4 mb-3"
          style={{
            background: 'rgba(16,185,129,0.08)',
            border: '1px solid rgba(16,185,129,0.25)',
          }}
          data-testid="ai-sources-block"
        >
          <div className="flex items-start gap-3">
            <BookOpen className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#10b981' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {t('generated.termsconsentmodal_grounded_in_real_a188')}
              </p>
              <p className="text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.termsconsentmodal_eili_s_answers_4e54')} <strong>{t('generated.termsconsentmodal_the_actual_bible_37af')}</strong> {t('generated.termsconsentmodal_verified_esv_bsb_31e6')} <strong>{t('generated.termsconsentmodal_documented_historical_and_5261')}</strong> {t('generated.termsconsentmodal_not_invented_by_ea3b')}
              </p>
              <ul className="text-xs mt-2 space-y-1 list-disc pl-4" style={{ color: 'var(--theme-textSecondary)' }}>
                <li>{t('generated.termsconsentmodal_every_scripture_quotation_f297')}</li>
                <li>{t('generated.termsconsentmodal_historical_and_cultural_351c')}</li>
                <li>{t('generated.termsconsentmodal_the_ai_composes_0b3f')} <em>around</em> {t('generated.termsconsentmodal_these_verified_sources_b348')}</li>
              </ul>
              <button
                onClick={() => navigate('/sources')}
                className="text-xs mt-2 underline font-medium"
                style={{ color: '#10b981' }}
                data-testid="view-sources-link"
              >
                {t('generated.termsconsentmodal_view_the_full_7d39')}
              </button>
            </div>
          </div>
        </div>

        {/* AI Data Sharing Disclosure — explicit per Apple 5.1.1(i) / 5.1.2(i) */}
        <div
          className="rounded-xl p-4 mb-3"
          style={{
            background: 'rgba(99,102,241,0.08)',
            border: '1px solid rgba(99,102,241,0.25)',
          }}
          data-testid="ai-disclosure-block"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {t('generated.termsconsentmodal_how_your_data_a694')}
              </p>
              <p className="text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.termsconsentmodal_to_generate_responses_1f01')} <strong>{t('generated.termsconsentmodal_your_chat_messages_e23c')}</strong> {t('generated.termsconsentmodal_are_sent_to_0669')} <strong>{t('generated.termsconsentmodal_google_gemini_52a3')}</strong>and <strong>{t('generated.termsconsentmodal_openai_gpt_93d9')}</strong>.
                              </p>
              <ul className="text-xs mt-2 space-y-1 list-disc pl-4" style={{ color: 'var(--theme-textSecondary)' }}>
                <li>{t('generated.termsconsentmodal_only_the_content_a06b')}</li>
                <li>{t('generated.termsconsentmodal_providers_are_contractually_c534')}</li>
                <li>{t('generated.termsconsentmodal_you_can_delete_0f19')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Disclaimer — accuracy warning */}
        <div
          className="rounded-xl p-4 mb-4"
          style={{
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.25)',
          }}
          data-testid="ai-disclaimer-block"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#f59e0b' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {t('generated.termsconsentmodal_still_verify_against_7bcb')}
              </p>
              <p className="text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.termsconsentmodal_even_with_verified_3a68')}
              </p>
            </div>
          </div>
        </div>

        <div
          className="rounded-xl p-4 mb-5 space-y-3"
          style={{ background: 'var(--glass-bg, rgba(15,23,42,0.4))', border: '1px solid var(--glass-border)' }}
        >
          <div className="flex items-start gap-3">
            <ScrollText className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t('generated.termsconsentmodal_terms_of_use_88f3')}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.termsconsentmodal_governs_your_use_8183')}
              </p>
              <button
                onClick={() => navigate('/terms')}
                className="text-xs mt-1 underline"
                style={{ color: 'var(--theme-accent)' }}
                data-testid="read-terms-link"
              >
                {t('generated.termsconsentmodal_read_full_terms_10f8')}
              </button>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Shield className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t('generated.termsconsentmodal_privacy_policy_7011')}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.termsconsentmodal_describes_what_data_18e5')}
              </p>
              <button
                onClick={() => navigate('/privacy')}
                className="text-xs mt-1 underline"
                style={{ color: 'var(--theme-accent)' }}
                data-testid="read-privacy-link"
              >
                {t('generated.termsconsentmodal_read_full_privacy_1965')}
              </button>
            </div>
          </div>
        </div>

        <p className="text-[11px] text-center mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
          {t('generated.termsconsentmodal_by_tapping_i_b9a6')}
        </p>

        <Button
          onClick={handleAccept}
          disabled={loading}
          className="w-full rounded-xl h-11 font-medium gap-2 text-white"
          style={{ background: 'var(--theme-accent, #6366f1)' }}
          data-testid="terms-accept-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('generated.termsconsentmodal_i_agree_continue_89d5')}
        </Button>
      </div>
    </div>
  );
}
